import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, type SortingStrategy } from '@dnd-kit/sortable'
import type { Issue } from '../api/types'
import { StateIcon } from '../components/Icons'
import { cx } from '../transcript/bits'
import { stateName } from '../vocab'
import { Card } from './Card'
import { columnId } from './dnd'
import { TERMINAL_WINDOW_DAYS, type ColumnData } from './filters'

const keepInPlace: SortingStrategy = () => null
export const SHOW_CLOSED = 'Show Cancelled and Duplicate'
export function Column({ column, ids, manual, issues, onToggleShowAll, onExpand }: {
  column: ColumnData; ids: readonly string[]; manual: boolean; issues: ReadonlyMap<string, Issue>; onToggleShowAll: (key: string) => void
  onExpand: () => void
}) {
  const { key, state } = column
  const { setNodeRef, isOver } = useDroppable({ id: columnId(key), data: { type: 'column', count: column.collapsed ? 0 : ids.length, thin: column.collapsed } })
  const name = column.column?.name ?? stateName(state)
  // Hidden, a closed column folds to a strip: its name and count, no cards; it still takes a drop.
  if (column.collapsed) return <section className={`column column-${state} column-thin`} aria-label={name}>
    <button type="button" ref={setNodeRef} className={cx('column-thin-button', isOver && 'is-over')} onClick={onExpand}
      aria-label={`${name}, ${column.total} ${column.total === 1 ? 'issue' : 'issues'}, hidden. ${SHOW_CLOSED}`} title={SHOW_CLOSED}>
      <StateIcon state={state} />
      <span className="column-count" aria-hidden="true">{column.total}</span>
      <span className="column-thin-name" aria-hidden="true">{name}</span>
    </button>
  </section>
  return <section className={`column column-${state}`} aria-labelledby={`column-title-${key}`}>
    <header className="column-header">
      <StateIcon state={state} /><h2 id={`column-title-${key}`}>{name}</h2>
      <span className="column-count" aria-label={`${ids.length} issues shown`}>{ids.length}</span>
      {column.terminal && (column.hidden > 0 || column.showAll) && <button type="button" className="column-toggle"
        onClick={() => onToggleShowAll(key)} title={column.showAll ? `Show the last ${TERMINAL_WINDOW_DAYS} days` : `Show all ${column.total} ${name} issues`}>
        {column.showAll ? `Last ${TERMINAL_WINDOW_DAYS} days` : `Show all ${column.total}`}
      </button>}
    </header>
    <SortableContext id={key} items={[...ids]} strategy={manual ? verticalListSortingStrategy : keepInPlace}>
      <div ref={setNodeRef} className={cx('column-body', isOver && 'is-over')}>
        {ids.map(id => { const issue = issues.get(id); return issue ? <Card key={id} issue={issue} /> : null })}
        {ids.length === 0 && <p className="column-empty">{column.hidden > 0 ? `Nothing in the last ${TERMINAL_WINDOW_DAYS} days` : 'No issues'}</p>}
      </div>
    </SortableContext>
  </section>
}
